import { useState, useEffect, useRef } from 'react';

interface UseCountUpProps {
  end: number;
  start?: number;
  duration?: number;
  delay?: number;
  isInView?: boolean;
}

export const useCountUp = ({
  end,
  start = 0,
  duration = 2000,
  delay = 0,
  isInView = true
}: UseCountUpProps) => {
  const [count, setCount] = useState(start);
  const countRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isInView) {
      setCount(start);
      return;
    }

    // Clear any existing animation
    if (countRef.current) {
      clearTimeout(countRef.current);
    }

    // Initial delay before starting the animation
    const delayTimeout = setTimeout(() => {
      startTimeRef.current = Date.now();
      
      const updateCount = () => {
        const now = Date.now();
        const elapsedTime = now - (startTimeRef.current || now);
        const progress = Math.min(elapsedTime / duration, 1);
        
        // Use easeOutQuad easing function for a more natural effect
        const easeProgress = 1 - (1 - progress) * (1 - progress);
        
        const nextCount = Math.floor(start + easeProgress * (end - start));
        setCount(nextCount);
        
        if (progress < 1) {
          // Continue the animation
          countRef.current = setTimeout(updateCount, 16); // Roughly 60fps
        } else {
          // Ensure we end exactly at the target number
          setCount(end);
        }
      };
      
      updateCount();
    }, delay);
    
    return () => {
      clearTimeout(delayTimeout);
      if (countRef.current) {
        clearTimeout(countRef.current);
      }
    };
  }, [end, start, duration, delay, isInView]);
  
  return count;
};
